import { useState } from 'react';
import { Plus, Edit2, Trash2, Info, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FeatureFlag, CreateFeatureFlagDto, RoleAccessPolicy, RoleAccessRule } from '@/types/feature-flags';
import { useFeatureFlags, useCreateFeatureFlag, useUpdateFeatureFlag, useDeleteFeatureFlag, useFeatureFlagRoleOptions } from '@/hooks/useFeatureFlags';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ProductFeatureFlagsTabProps {
  productId: string;
}


export default function ProductFeatureFlagsTab({ productId }: ProductFeatureFlagsTabProps) {
  const { t } = useTranslation();
  const createEmptyRoleRules = (): RoleAccessPolicy => ({
    provider: [],
    customer: [],
    team: [],
  });
  
  // Local state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  
  // React Query hooks
  const { data: featureFlagsData, isLoading, error } = useFeatureFlags(productId);
  const createFeatureFlag = useCreateFeatureFlag();
  const updateFeatureFlag = useUpdateFeatureFlag();
  const deleteFeatureFlag = useDeleteFeatureFlag();
  const { data: roleOptionsData, isLoading: isRoleOptionsLoading } = useFeatureFlagRoleOptions(productId);
  const roleOptions = roleOptionsData ?? {
    provider: [],
    customer: [],
    team: [],
    effects: ['allow', 'deny'],
  };

  // Form state for create/edit
  const [formData, setFormData] = useState<CreateFeatureFlagDto>({
    key: '',
    name: '',
    description: '',
    isVisible: true,
    sortOrder: 0,
    roleRules: createEmptyRoleRules(),
  });

  const featureFlags = featureFlagsData?.data || [];

  const handleCreateFlag = () => {
    setFormData({
      key: '',
      name: '',
      description: '',
      isVisible: true,
      sortOrder: 0,
      roleRules: createEmptyRoleRules(),
    });
    setCreateError(null);
    setIsCreateDialogOpen(true);
  };

  const handleEditFlag = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setFormData({
      key: flag.key,
      name: flag.name,
      description: flag.description || '',
      isVisible: flag.isVisible,
      sortOrder: flag.sortOrder,
      roleRules: {
        provider: [...(flag.roleRules?.provider ?? [])],
        customer: [...(flag.roleRules?.customer ?? [])],
        team: [...(flag.roleRules?.team ?? [])],
      },
    });
    setEditError(null);
    setIsEditDialogOpen(true);
  };

  const handleDeleteFlag = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setIsDeleteDialogOpen(true);
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  type RoleActor = 'provider' | 'customer' | 'team';

  const handleAddRoleRule = (actor: RoleActor) => {
    const availableRoles = roleOptions[actor] ?? [];
    if (availableRoles.length === 0) return;

    const existing = formData.roleRules?.[actor] ?? [];
    const nextRole = availableRoles.find(
      (role) => !existing.some((rule) => rule.role === role)
    ) ?? availableRoles[0];
    const nextEffect = roleOptions.effects?.[0] ?? 'allow';

    setFormData((prev) => ({
      ...prev,
      roleRules: {
        ...prev.roleRules,
        [actor]: [...existing, { role: nextRole, effect: nextEffect }],
      },
    }));
  };

  const handleRoleRuleChange = (
    actor: RoleActor,
    index: number,
    field: 'role' | 'effect',
    value: RoleAccessRule['role'] | RoleAccessRule['effect']
  ) => {
    setFormData((prev) => {
      const bucket = [...(prev.roleRules?.[actor] ?? [])];
      if (!bucket[index]) return prev;

      if (field === 'role') {
        const duplicate = bucket.some(
          (rule, ruleIndex) => ruleIndex !== index && rule.role === value
        );
        if (duplicate) return prev;
      }

      bucket[index] = { ...bucket[index], [field]: value } as RoleAccessRule;

      return {
        ...prev,
        roleRules: {
          ...prev.roleRules,
          [actor]: bucket,
        },
      };
    });
  };

  const handleRemoveRoleRule = (actor: RoleActor, index: number) => {
    setFormData((prev) => {
      const bucket = [...(prev.roleRules?.[actor] ?? [])];
      bucket.splice(index, 1);

      return {
        ...prev,
        roleRules: {
          ...prev.roleRules,
          [actor]: bucket,
        },
      };
    });
  };

  const extractErrorMessage = (error: unknown): string => {
    const fallback = t('products.featureFlagSaveError');

    if (error && typeof error === 'object') {
      const errObj = error as { response?: { data?: unknown } ; message?: string; data?: unknown };
      const data = (errObj.response?.data ?? errObj.data) as Record<string, unknown> | undefined;
      const topMessage = typeof data?.message === 'string' ? data.message : undefined;

      const fieldErrors = data?.errors;
      const fieldMessages: string[] = [];
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((err) => {
          if (err && typeof err === 'object') {
            const detail = (err as { detail?: string; title?: string; message?: string }).detail
              ?? (err as { title?: string }).title
              ?? (err as { message?: string }).message;
            if (typeof detail === 'string') {
              fieldMessages.push(detail);
            }
          }
        });
      } else if (fieldErrors && typeof fieldErrors === 'object') {
        Object.values(fieldErrors).forEach((val) => {
          if (Array.isArray(val)) {
            val.forEach((msg) => {
              if (typeof msg === 'string') fieldMessages.push(msg);
            });
          } else if (typeof val === 'string') {
            fieldMessages.push(val);
          }
        });
      }

      if (topMessage) return topMessage;
      if (fieldMessages.length) return fieldMessages[0];
      if (errObj.message) return errObj.message;
    }

    return fallback;
  };

  const renderRoleRulesFields = () => {
    const roleActors: { key: RoleActor; label: string }[] = [
      { key: 'customer', label: t('products.roleRulesCustomerLabel') },
      { key: 'team', label: t('products.roleRulesTeamLabel') },
      { key: 'provider', label: t('products.roleRulesProviderLabel') },
    ];
    const actionOptions = roleOptions.actions?.length ? roleOptions.actions : ['view', 'create', 'update', 'delete', 'manage'];
    const baseActions = actionOptions.filter((action) => action !== 'manage');

    const toggleActions = (actor: RoleActor, index: number, action: string) => {
      setFormData((prev) => {
        const bucket = [...(prev.roleRules?.[actor] ?? [])];
        const existingRule = bucket[index];
        if (!existingRule) return prev;

        const currentActions = existingRule.actions ? [...existingRule.actions] : [];

        let nextActions: string[] | undefined = currentActions;

        if (action === 'manage') {
          const hasManage = currentActions.includes('manage');
          nextActions = hasManage ? [] : ['manage'];
        } else {
          const hasManage = currentActions.includes('manage');
          const filtered = hasManage ? [] : currentActions.filter((a) => a !== action);
          if (hasManage) {
            nextActions = [action];
          } else if (currentActions.includes(action)) {
            nextActions = filtered;
          } else {
            nextActions = [...currentActions.filter((a) => a !== 'manage'), action];
          }
        }

        // If all base actions selected, treat as "all" (undefined) so backend assumes all actions
        const allBaseSelected =
          nextActions &&
          nextActions.length > 0 &&
          !nextActions.includes('manage') &&
          baseActions.every((action) => nextActions?.includes(action));

        const normalizedActions =
          nextActions && nextActions.length > 0
            ? nextActions.includes('manage')
              ? ['manage']
              : allBaseSelected
              ? undefined
              : nextActions
            : undefined;

        bucket[index] = {
          ...existingRule,
          actions: normalizedActions,
        };

        return {
          ...prev,
          roleRules: {
            ...prev.roleRules,
            [actor]: bucket,
          },
        };
      });
    };

    return (
      <div className="space-y-3 rounded-md border p-4">
        <div className="space-y-1">
          <Label>{t('products.roleRulesLabel')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('products.roleRulesDescription')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('products.roleRulesEmptyHint')}
          </p>
        </div>
        <div className="space-y-3">
          {roleActors.map(({ key, label }) => {
            const rules = formData.roleRules?.[key] ?? [];
            const availableRoles = roleOptions[key] ?? [];
            const allRolesUsed = availableRoles.length > 0 && rules.length >= availableRoles.length;
            return (
              <div key={key} className="space-y-2 rounded-md border border-dashed p-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold">{label}</p>
                    <p className="text-xs text-muted-foreground">
                      {rules.length === 0 ? t('products.roleRulesAllRoles') : t('products.roleRulesBucketHint')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddRoleRule(key)}
                    disabled={isRoleOptionsLoading || availableRoles.length === 0 || allRolesUsed}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    {t('products.addRoleRule')}
                  </Button>
                </div>
                {rules.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('products.roleRulesAllRoles')}</p>
                ) : (
                  <div className="space-y-2">
                    {rules.map((rule, index) => {
                      const disabledRoles = new Set(
                        rules.filter((_, i) => i !== index).map((r) => r.role)
                      );

                      const describeActions = (actions?: string[]) => {
                        if (!actions || actions.length === 0) return t('products.roleRulesAllActions', 'All actions');
                        if (actions.includes('manage')) return t('products.roleRulesManage', 'Manage (all)');
                        const allSelected = actionOptions
                          .filter((action) => action !== 'manage')
                          .every((action) => actions.includes(action));
                        if (allSelected) return t('products.roleRulesAllActions', 'All actions');
                        return actions.join(', ');
                      };
                      return (
                        <div key={`${key}-${index}`} className="grid grid-cols-12 items-center gap-2">
                          <div className="col-span-4">
                            <Select
                              value={rule.role}
                              onValueChange={(value) => handleRoleRuleChange(key, index, 'role', value)}
                              disabled={isRoleOptionsLoading || availableRoles.length === 0}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t('products.selectRolePlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                {availableRoles.map((roleOption) => (
                                  <SelectItem
                                    key={roleOption}
                                    value={roleOption}
                                    disabled={disabledRoles.has(roleOption)}
                                  >
                                    {roleOption}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-3">
                            <Select
                              value={rule.effect ?? 'allow'}
                              onValueChange={(value) => handleRoleRuleChange(key, index, 'effect', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t('products.selectEffectPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                {(roleOptions.effects?.length ? roleOptions.effects : ['allow', 'deny']).map((effect) => (
                                  <SelectItem key={effect} value={effect}>
                                    {effect === 'allow' ? t('products.roleRuleEffectAllow') : t('products.roleRuleEffectDeny')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4">
                            <div className="space-y-1.5">
                              <p className="text-xs font-medium text-muted-foreground">
                                {t('products.roleRulesActionsLabel', 'Actions')}
                              </p>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-between"
                                  >
                                    <span className="truncate text-left">{describeActions(rule.actions)}</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-56">
                                  <DropdownMenuLabel>
                                    {t('products.roleRulesActionsLabel', 'Actions')}
                                  </DropdownMenuLabel>
                                  <DropdownMenuCheckboxItem
                                    checked={rule.actions?.includes('manage') ?? false}
                                    onCheckedChange={() => toggleActions(key, index, 'manage')}
                                  >
                                    {t('products.roleRulesManage', 'Manage (all)')}
                                  </DropdownMenuCheckboxItem>
                                  <DropdownMenuSeparator />
                                  {actionOptions
                                    .filter((action) => action !== 'manage')
                                    .map((action) => (
                                      <DropdownMenuCheckboxItem
                                        key={action}
                                        checked={rule.actions?.includes(action) ?? false}
                                        onCheckedChange={() => toggleActions(key, index, action)}
                                      >
                                        {action}
                                      </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <p className="text-[11px] text-muted-foreground">
                                {describeActions(rule.actions)}
                              </p>
                            </div>
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveRoleRule(key, index)}
                              aria-label={t('products.removeRoleRule')}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRoleRulesSummary = (policy: RoleAccessPolicy) => {
    const roleActors: { key: RoleActor; label: string }[] = [
      { key: 'customer', label: t('products.roleRulesCustomerLabel') },
      { key: 'team', label: t('products.roleRulesTeamLabel') },
      { key: 'provider', label: t('products.roleRulesProviderLabel') },
    ];
    const actionOptions = roleOptions.actions?.length ? roleOptions.actions : ['view', 'create', 'update', 'delete', 'manage'];
    const allBaseActions = actionOptions.filter((action) => action !== 'manage');
    const allActionsLabel = t('products.roleRulesAllActions', 'All actions');

    const hasAnyRules = roleActors.some((actor) => (policy?.[actor.key] ?? []).length > 0);

    if (!hasAnyRules) {
      return <span className="text-xs text-muted-foreground">{t('products.roleRulesAllRoles')}</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        {roleActors.map(({ key, label }) => {
          const rules = policy?.[key] ?? [];
          if (!rules.length) return null;

          return (
            <div key={key} className="flex flex-wrap items-center gap-1">
              <span className="text-xs font-semibold">{label}:</span>
              {rules.map((rule, index) => (
                <Badge
                  key={`${rule.role}-${index}`}
                  variant={rule.effect === 'deny' ? 'secondary' : 'outline'}
                >
                  {rule.role}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({rule.effect ?? 'allow'}
                    {(() => {
                      const actions = rule.actions;
                      if (!actions || actions.length === 0) return ` · ${allActionsLabel}`;
                      if (actions.includes('manage')) return ' · manage';
                      const allSelected = allBaseActions.every((action) => actions.includes(action));
                      if (allSelected) return ` · ${allActionsLabel.toLowerCase()}`;
                      return ` · ${actions.join(', ')}`;
                    })()}
                    )
                  </span>
                </Badge>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const handleSubmitCreate = async () => {
    try {
      await createFeatureFlag.mutateAsync({
        productId,
        data: formData
      });
      setIsCreateDialogOpen(false);
      setCreateError(null);
      setFormData({
        key: '',
        name: '',
        description: '',
        isVisible: true,
        sortOrder: 0,
        roleRules: createEmptyRoleRules(),
      });
    } catch (error) {
      setCreateError(extractErrorMessage(error));
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedFlag) return;
    
    try {
      await updateFeatureFlag.mutateAsync({
        productId,
        flagId: selectedFlag.id,
        data: formData
      });
      setIsEditDialogOpen(false);
      setEditError(null);
    } catch (error) {
      setEditError(extractErrorMessage(error));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedFlag) return;
    
    try {
      await deleteFeatureFlag.mutateAsync({
        productId,
        flagId: selectedFlag.id
      });
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete feature flag:', error);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (only show error if it's not just an empty response)
  if (error && !featureFlagsData) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t('common.errorLoadingData')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {t('products.featureFlagsAllowControl')}
        </AlertDescription>
      </Alert>

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('products.featureFlags')}</CardTitle>
              <CardDescription>
                {t('products.featureFlagsDescription')}
              </CardDescription>
            </div>
            <Button onClick={handleCreateFlag}>
              <Plus className="h-4 w-4 mr-2" />
              {t('products.addFeatureFlag')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {featureFlags.length === 0 && !isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                {t('products.noFeatureFlagsYet')}
              </p>
              <Button onClick={handleCreateFlag}>
                <Plus className="h-4 w-4 mr-2" />
                {t('products.addFeatureFlag')}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('products.flagKey')}</TableHead>
                  <TableHead>{t('products.name')}</TableHead>
                  <TableHead>{t('common.description')}</TableHead>
                  <TableHead>{t('products.roleRulesColumnLabel')}</TableHead>
                  <TableHead>{t('products.visibility')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {featureFlags.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {flag.key}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyKey(flag.key)}
                        >
                          {copiedKey === flag.key ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{flag.name}</TableCell>
                    <TableCell className="max-w-xs">
                      <span className="line-clamp-2 text-sm text-muted-foreground">
                        {flag.description || t('common.noDescription')}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {renderRoleRulesSummary(flag.roleRules)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={flag.isVisible ? "default" : "secondary"}>
                        {flag.isVisible ? t('products.visible') : t('products.hidden')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditFlag(flag)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFlag(flag)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* API Integration Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('products.apiIntegration')}</CardTitle>
          <CardDescription>
            {t('products.howToUseFeatureFlags')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('products.endpoint')}</Label>
            <code className="block mt-1 p-2 bg-muted rounded text-sm">
              {t('products.featureFlagsApiRoute', 'GET /api/v1/customers/{{customerId}}/feature-flags', {
                customerId: t('products.customerIdParam'),
              })}
            </code>
          </div>
          <div>
            <Label>{t('products.responseExample')}</Label>
            <pre className="mt-1 p-2 bg-muted rounded text-sm overflow-x-auto">
{`{
  "customerId": "cust_123",
  "${t('products.activeFeatures')}": [
    "bulk-export",
    "advanced-analytics",
    "api-webhooks"
  ],
  "${t('products.salesOrderId')}": "so_456",
  "${t('products.effectiveDate')}": "2024-01-15T10:00:00Z"
}`}
            </pre>
          </div>
            <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('products.featureFlagsStoredInfo')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('products.addFeatureFlagTitle')}</DialogTitle>
            <DialogDescription>
              {t('products.createFeatureFlag')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {createError && (
              <Alert variant="destructive">
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="key">{t('products.flagKeyLabel')}</Label>
              <Input
                id="key"
                placeholder={t('products.flagKeyPlaceholder')}
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {t('products.flagKeyHint')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('products.nameLabel')}</Label>
              <Input
                id="name"
                placeholder={t('products.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('products.featureFlagDescriptionLabel')}</Label>
              <Textarea
                id="description"
                placeholder={t('products.featureFlagDescriptionPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            {renderRoleRulesFields()}
            <div className="flex items-center space-x-2">
              <Switch
                id="isVisible"
                checked={formData.isVisible}
                onCheckedChange={(checked) => setFormData({ ...formData, isVisible: checked })}
              />
              <Label htmlFor="isVisible">
                {t('products.visibilityLabel')}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSubmitCreate}
              disabled={createFeatureFlag.isPending}
            >
              {createFeatureFlag.isPending && (
                <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full mr-2"></div>
              )}
              {t('products.createFeatureFlagButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('products.editFeatureFlagTitle')}</DialogTitle>
            <DialogDescription>
              {t('products.updateFeatureFlag')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editError && (
              <Alert variant="destructive">
                <AlertDescription>{editError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-key">{t('products.flagKeyLabel')}</Label>
              <Input
                id="edit-key"
                value={formData.key}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('products.nameLabel')}</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">{t('products.featureFlagDescriptionLabel')}</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            {renderRoleRulesFields()}
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-isVisible"
                checked={formData.isVisible}
                onCheckedChange={(checked) => setFormData({ ...formData, isVisible: checked })}
              />
              <Label htmlFor="edit-isVisible">
                {t('products.visibilityLabel')}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSubmitEdit}
              disabled={updateFeatureFlag.isPending}
            >
              {updateFeatureFlag.isPending && (
                <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full mr-2"></div>
              )}
              {t('products.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.deleteFeatureFlagConfirmation', { name: selectedFlag?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={deleteFeatureFlag.isPending}
            >
              {deleteFeatureFlag.isPending && (
                <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full mr-2"></div>
              )}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
